import { ChangeEvent, useState, useContext } from 'react'
import ManageSearchIcon from '@mui/icons-material/ManageSearch'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { Tooltip, Checkbox, ListItemButton, ListItemIcon, FormControlLabel } from '@mui/material'
import { ReadGridToolbarContext } from '@datasets/handsontable/read-grid/context/toolbar'
import { Selected, Field, RowPropertyName } from '../../types'
import { ListItemLabel } from '../listItemLabel'
import * as css from './styles'
import { t } from '@lingui/core/macro'

interface MultiGridRef {
  props: {
    columns: { fieldId: number; name: string; [key: string]: unknown }[]
  }
  _topRightGrid: {
    scrollToCell: (options: { columnIndex: number }) => void
  }
}

interface IProps {
  fields: Field[]
  selected: Selected
  handleSelect: ({
    e,
    fieldId,
    rowPropertyName,
  }: {
    e: ChangeEvent<HTMLInputElement>
    fieldId: number
    rowPropertyName?: RowPropertyName
  }) => void
  closePopover: () => void
}

export const FilteredColumns = ({ fields, selected, handleSelect, closePopover }: IProps) => {
  const [ellipsisActive, setEllipsisActive] = useState(false)
  const { gridRef } = useContext(ReadGridToolbarContext)

  // We need a way to know the visible column headers;
  // they drop off from the DOM with the virtualized Gird so we cannot use
  // a querySelector.
  const multiGridRef = gridRef?.current as MultiGridRef | null
  const headers = multiGridRef?.props.columns.filter(col => col.fieldId >= 0)

  /**
   * Onclick function to go to the header.
   * - Add pulse animation class for header.
   * @param {string} name The name of the header.
   */
  function goToHeaderColumn(name: string) {
    const columnIndex = headers?.findIndex(header => header.name === name)

    if (columnIndex === -1 || columnIndex === undefined || !multiGridRef) return

    multiGridRef._topRightGrid.scrollToCell({ columnIndex: +columnIndex })

    // Virtualized Grid header is not in DOM until scrollToCell has jumped.
    setTimeout(() => {
      const $header = document.querySelector(`[data-read-grid-header-name="${name}"]`)
      $header?.classList.add('-go-to-pulse')
    }, 0)

    closePopover()
  }

  return (
    <>
      {fields.map(field => {
        const { fieldId, name, visible } = field
        const rowPropertyName = field.rowPropertyName
        const key = rowPropertyName ?? fieldId
        const showHiddenIcon = !visible && !rowPropertyName
        const isVisibleHeader = headers?.findIndex(field => field.name === name) !== -1
        const isSelected = selected[rowPropertyName ?? fieldId] ?? false

        return (
          <ListItemButton
            key={key}
            disableRipple
            css={[css.listItemButtonSmall, css.fullWidth]}
            data-testid="filtered-list"
          >
            <ListItemIcon css={css.listItemIcon} data-testid="filtered-item-icon">
              <FormControlLabel
                css={ellipsisActive ? css.labelOverflow : css.label}
                label={<ListItemLabel name={name} setEllipsisActive={setEllipsisActive} />}
                data-testid="filtered-form-label"
                control={
                  <Checkbox
                    color="secondary"
                    size="small"
                    checked={isSelected}
                    onChange={e => {
                      handleSelect({ e, fieldId, rowPropertyName })
                    }}
                    data-testid="filtered-checkbox"
                    edge="start"
                    disableRipple
                  />
                }
              />
            </ListItemIcon>
            {showHiddenIcon && (
              <Tooltip title={t`Default hidden field`} data-testid="filtered-tooltip" arrow>
                <VisibilityOff
                  color="disabled"
                  css={css.visibilityIcon}
                  data-testid="visibility-off-icon"
                />
              </Tooltip>
            )}
            {isVisibleHeader && (
              <Tooltip title={t`Go to column`} data-testid="filtered-tooltip" arrow>
                <ManageSearchIcon
                  color="primary"
                  onClick={() => {
                    goToHeaderColumn(name)
                  }}
                  data-testid="manager-search-icon"
                />
              </Tooltip>
            )}
          </ListItemButton>
        )
      })}
    </>
  )
}
